import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useRouteModalOpen } from '../context/RouteModalContext'

export interface TourStep {
	/** CSS-селектор для десктопа */
	targetSelector: string
	/** CSS-селектор для мобайла (если отличается) */
	mobileTargetSelector?: string
	title: string
	description: React.ReactNode
	nextLabel: string
	/** Когда true — шаг авто-завершается (пользователь выполнил действие) */
	completed?: boolean
}

interface Props {
	steps: TourStep[]
	storageKey: string
	onFinish: () => void
	onClosingStart?: () => void
}

const PADDING = 10
const AUTO_ADVANCE_DELAY = 700

function isMobile() {
	return window.innerWidth <= 768
}

const FADE_OUT_DURATION = 350

export default function SpotlightTour({ steps, storageKey, onFinish, onClosingStart }: Props) {
	// Поверх страницы открыто окно тарифов или входа (11.10): тур ждёт — не рисуется над окном, не листает страницу под
	// ним и не пропускает шаги; закрыли окно — продолжает с того же шага.
	const paused = useRouteModalOpen()
	const [stepIndex, setStepIndex] = useState(0)
	const [rect, setRect] = useState<DOMRect | null | undefined>(undefined)
	const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null)
	const [justCompleted, setJustCompleted] = useState(false)
	const [closing, setClosing] = useState(false)
	const tooltipRef = useRef<HTMLDivElement>(null)
	const step = steps[stepIndex]

	// Объявлены до эффектов, которые их зовут (авто-переход, пропуск шага без цели).
	function advance() {
		if (stepIndex < steps.length - 1) {
			setStepIndex((i) => i + 1)
		} else {
			finish()
		}
	}

	function finish() {
		localStorage.setItem(storageKey, '1')
		setClosing(true)
		onClosingStart?.()
		setTimeout(() => onFinish(), FADE_OUT_DURATION)
	}

	// ── Находим целевой элемент ───────────────────────────────
	// При смене шага:
	//   1. Ищем target в DOM
	//   2. Скроллим его ближайший scroll-ancestor (если есть — например,
	//      .anchored-popover__content когда popover с max-height + overflow)
	//      и window, чтобы target оказался в видимой области
	//   3. Слушаем scroll (capture: true — ловит scroll внутри popover'а) +
	//      resize, чтобы rect обновлялся если анимация/юзер-скролл двигают
	//      элемент после первого замера
	useLayoutEffect(() => {
		if (paused) return
		// eslint-disable-next-line react-hooks/set-state-in-effect -- замер цели шага в DOM: сброс перед новым замером
		setRect(undefined)
		if (!step) { setRect(null); return }
		const selector = isMobile() && step.mobileTargetSelector
			? step.mobileTargetSelector
			: step.targetSelector
		const el = document.querySelector(selector) as HTMLElement | null
		if (!el) { setRect(null); return }

		// Прокрутить target в вид — instant, без smooth, чтобы rect был
		// точным сразу после вызова. Сохраняет нас от ситуации «tour-
		// highlight в правильном месте, но target за пределами видимой
		// области popover'а» (контент скроллимый, без scrollIntoView
		// юзер не видит подсвеченный элемент).
		try {
			el.scrollIntoView({ block: 'center', inline: 'center', behavior: 'instant' as ScrollBehavior })
		} catch {
			// Старые браузеры без instant — fallback на smooth (можем
			// промахнуться с rect'ом на тик, обновится через scroll listener)
			el.scrollIntoView({ block: 'center', inline: 'center' })
		}

		const update = () => {
			const fresh = document.querySelector(selector)
			setRect(fresh ? fresh.getBoundingClientRect() : null)
		}
		update()

		// Трекаем scroll (capture — чтобы ловить overflow-скролл внутри
		// popover'ов/модалок) и resize → пересчитываем rect.
		window.addEventListener('scroll', update, true)
		window.addEventListener('resize', update)
		return () => {
			window.removeEventListener('scroll', update, true)
			window.removeEventListener('resize', update)
		}
	}, [stepIndex, step, paused])

	// ── Позиционируем тултип ──────────────────────────────────
	useLayoutEffect(() => {
		if (!rect || !tooltipRef.current) return
		const tw = tooltipRef.current.offsetWidth || 300
		const th = tooltipRef.current.offsetHeight || 150
		const vw = window.innerWidth
		const vh = window.innerHeight
		const gap = 14

		let top: number
		if (rect.bottom + th + gap <= vh - 16) {
			top = rect.bottom + gap
		} else if (rect.top - th - gap >= 16) {
			top = rect.top - th - gap
		} else {
			top = Math.max(16, vh / 2 - th / 2)
		}

		let left = rect.left + rect.width / 2 - tw / 2
		left = Math.max(16, Math.min(vw - tw - 16, left))
		// eslint-disable-next-line react-hooks/set-state-in-effect -- место тултипа считается по замеру его размеров в DOM
		setTooltipPos({ top, left })
	}, [rect])

	// ── Авто-переход когда шаг выполнен ──────────────────────
	useEffect(() => {
		if (!step?.completed || justCompleted) return
		// eslint-disable-next-line react-hooks/set-state-in-effect -- шаг выполнили действием на странице: «готово», затем переход
		setJustCompleted(true)
		const t = setTimeout(() => {
			setJustCompleted(false)
			advance()
		}, AUTO_ADVANCE_DELAY)
		return () => clearTimeout(t)
	}, [step?.completed]) // eslint-disable-line react-hooks/exhaustive-deps

	// Сброс justCompleted при смене шага
	useEffect(() => {
		// eslint-disable-next-line react-hooks/set-state-in-effect -- шаг меняют и «Дальше», и «Назад», и авто-переход
		setJustCompleted(false)
	}, [stepIndex])

	// Элемент не найден или скрыт (0×0) — пропускаем шаг
	useEffect(() => {
		// eslint-disable-next-line react-hooks/set-state-in-effect -- цели шага нет в DOM (замер дал null/0×0) — шаг пропускается
		if (rect === null || (rect !== undefined && rect.width === 0 && rect.height === 0)) advance()
	}, [rect]) // eslint-disable-line react-hooks/exhaustive-deps

	// Клики по элементам тура НЕ должны всплывать к underlying-модалке. Тур
	// рендерится через portal к document.body, но в React-fiber-дереве он —
	// потомок модалки (Sheet/CardModal/popover), а синтетические события идут
	// по fiber, НЕ по DOM. Без stopPropagation клик «Дальше»/«Назад»/панель
	// всплывал в onClick родителя и закрывал его (баг: тур закрывал CardModal
	// + ронял scroll-lock). stopPropagation на тултипе ловит все кнопки внутри.
	const dismiss = (e: React.MouseEvent) => { e.stopPropagation(); finish() }
	const stop = (e: React.MouseEvent) => e.stopPropagation()

	if (paused) return null
	if (rect === undefined || rect === null || (rect.width === 0 && rect.height === 0)) return null

	// Зажимаем highlight внутри viewport
	const hlTop    = Math.max(0, rect.top    - PADDING)
	const hlLeft   = Math.max(0, rect.left   - PADDING)
	const hlRight  = Math.min(window.innerWidth,  rect.right  + PADDING)
	const hlBottom = Math.min(window.innerHeight, rect.bottom + PADDING)

	const vw = window.innerWidth
	const vh = window.innerHeight
	const closingCls = closing ? ' spt__panel--closing' : ''

	return (
		<>
			{/* 4 панели вокруг highlight — каждая со своим blur, не затрагивают выделенную зону */}
			<div className={`spt__panel${closingCls}`} style={{ top: 0,       left: 0,        width: vw,            height: hlTop                }} onClick={dismiss} />
			<div className={`spt__panel${closingCls}`} style={{ top: hlBottom, left: 0,        width: vw,            height: vh - hlBottom        }} onClick={dismiss} />
			<div className={`spt__panel${closingCls}`} style={{ top: hlTop,    left: 0,        width: hlLeft,        height: hlBottom - hlTop      }} onClick={dismiss} />
			<div className={`spt__panel${closingCls}`} style={{ top: hlTop,    left: hlRight,  width: vw - hlRight,  height: hlBottom - hlTop      }} onClick={dismiss} />

			{/* Подсветка целевого элемента */}
			<div
				className={`spt__highlight${justCompleted ? ' spt__highlight--done' : ''}${closing ? ' spt__highlight--closing' : ''}`}
				style={{
					top:    hlTop,
					left:   hlLeft,
					width:  hlRight  - hlLeft,
					height: hlBottom - hlTop,
				}}
			/>

			{/* Тултип */}
			<div
				ref={tooltipRef}
				className={`spt__tooltip${justCompleted ? ' spt__tooltip--done' : ''}${closing ? ' spt__tooltip--closing' : ''}`}
				onClick={stop}
				style={tooltipPos
					? { top: tooltipPos.top, left: tooltipPos.left }
					: { visibility: 'hidden', top: 0, left: 0 }
				}
			>
				{/* Прогресс + кнопка пропустить */}
				<div className="spt__tooltip-top">
					<div className="spt__dots">
						{steps.map((_, i) => (
							<span
								key={i}
								className={`spt__dot${i === stepIndex ? ' spt__dot--active' : i < stepIndex ? ' spt__dot--done' : ''}`}
							/>
						))}
					</div>
					<button className="spt__skip" onClick={finish}>Пропустить</button>
				</div>

				{/* Контент шага */}
				{justCompleted ? (
					<div className="spt__done-msg">
						<span className="spt__checkmark">✓</span>
						Отлично, идём дальше!
					</div>
				) : (
					<>
						<h3 className="spt__title">{step.title}</h3>
						<div className="spt__desc">{step.description}</div>
						<div className="spt__buttons">
							{stepIndex > 0 && (
								<button className="spt__back" onClick={() => setStepIndex(i => i - 1)}>
									← Назад
								</button>
							)}
							<button className="spt__next" onClick={advance}>
								{step.nextLabel}
							</button>
						</div>
					</>
				)}
			</div>

		</>
	)
}
